/**
 * Character styles: build, palette, clothing, hair and face for the shared character rig. Palettes are
 * drawn from the existing art direction — the player's camel overcoat, the pedestrian coat/trouser
 * lists (src/peds/pedSim.ts) and the constabulary navy (src/police/copView.ts): muted late-60s /
 * early-70s British — camel, navy, brown, bottle green, mustard, grey.
 *
 * Pedestrians come from archetypes (dockers, clerks, shop girls, pensioners, mods, skinheads, the
 * milkman, the vicar …), weighted per district, with seeded colours, hair and faces.
 */

export type CharacterBuild = 'slim' | 'average' | 'stocky' | 'heavy';
export type Headwear = 'flatCap' | 'trilby' | 'none' | 'policeHelmet' | 'headscarf' | 'peakedCap' | 'bowler';
/**
 * Clothing cut. The first five are the originals; the rest were added in the second character pass:
 * suit (lounge suit and tie), knit (jumper over a shirt), donkeyJacket (with the black shoulder yoke),
 * harrington (short zip jacket), parka (fishtail, hood roll), miniSkirt (short dress / skirt suit),
 * clerical (black suit and dog collar), workCoat (shop or dairy coat), armed (police blouson).
 */
export type Outfit =
  | 'longCoat'
  | 'jacket'
  | 'shirtsleeves'
  | 'dressSkirt'
  | 'tunic'
  | 'suit'
  | 'knit'
  | 'donkeyJacket'
  | 'harrington'
  | 'parka'
  | 'miniSkirt'
  | 'clerical'
  | 'workCoat'
  | 'armed';
/** Period hair: short back and sides, side parting, quiff, mod mop, long, beehive, bob, balding, crop, afro. */
export type HairStyle = 'short' | 'sidePart' | 'quiff' | 'mod' | 'long' | 'beehive' | 'bob' | 'balding' | 'crop' | 'afro';
export type FacialHair = 'none' | 'moustache' | 'sideburns' | 'mutton' | 'beard';
export type Footwear = 'shoes' | 'boots' | 'heels';
/** Something carried in the off hand (pedestrians only). */
export type CarriedProp = 'none' | 'briefcase' | 'handbag' | 'stick' | 'newspaper' | 'crate' | 'shopping' | 'umbrella';

export interface CharacterPalette {
  /** Outer layer: overcoat, jacket, dress or tunic. */
  coat: number;
  trousers: number;
  shoes: number;
  skin: number;
  hair: number;
  /** Shirt front / collar / cuffs. */
  shirt: number;
  /** Cap, hat, scarf or helmet. */
  headwear: number;
  /** Small accent: tie, hat band, buttons, yoke, helmet crest. */
  accent: number;
}

export interface CharacterStyle {
  /** Authored player sculpt; never enabled by pedestrian or police presets. */
  sculpt?: 'villain';
  build: CharacterBuild;
  /** Height multiplier around 1 (≈0.9–1.1). */
  height: number;
  palette: CharacterPalette;
  headwear: Headwear;
  outfit: Outfit;
  /** Period facial hair (moustache + long sideburns). Kept for older styles; `facial` wins when set. */
  moustache?: boolean;
  facial?: FacialHair;
  /** Default: from outfit (skirts → bob, otherwise short back and sides). */
  hair?: HairStyle;
  /** 0 = young … 1 = pensioner (posture, gait, grey hair). Default 0.3. */
  age?: number;
  /** Women's cut: softer jaw, lipstick, narrower shoulders, hip sway. Default: skirt outfits. */
  female?: boolean;
  tie?: boolean;
  glasses?: boolean;
  /** Cigarette in hand; idles with a smoke. */
  smoker?: boolean;
  shoesKind?: Footwear;
  prop?: CarriedProp;
  /** Work kit worn over the clothes: a shop or bar apron, or a bus conductor's ticket machine and cash bag. */
  kit?: 'none' | 'apron' | 'conductor';
  /** Colour of the apron (sRGB hex); default a cream cloth. */
  kitColour?: number;
  /** 0..1 swagger in the walk and stance (mods, skinheads, rockers, the villain). Animation only. */
  swagger?: number;
}

/** Minimal RNG interface (src/core/rng.ts `Rng` satisfies it). */
export interface StyleRng {
  next(): number;
}

// Shared swatches (hex). Kept in one place so presets and the random generator stay coherent.
export const SWATCH = {
  camel: 0x8a6440,
  tobacco: 0x6b4a2f,
  navy: 0x2b3b5c,
  policeNavy: 0x18213a,
  policeNavyDark: 0x10172a,
  bottleGreen: 0x36524f,
  olive: 0x4b5a3a,
  oxblood: 0x7a2d24,
  mustard: 0xb8964a,
  stone: 0xc9b79a,
  grey: 0x8c8c86,
  charcoal: 0x23252b,
  plum: 0x5c3b4e,
  black: 0x1f1f24,
  shoeBlack: 0x15110e,
  shoeBrown: 0x3a2618,
  shirtWhite: 0xd8d2c4,
  capTweed: 0x3b3a33,
  brass: 0xc9a24a,
  parkaOlive: 0x55583a,
  donkey: 0x22262a,
  dairyWhite: 0xd9d5c8,
  clergy: 0x17171b,
} as const;

/** Outer-layer colours for pedestrians (matches the pedestrian COATS list). */
export const PED_COATS: readonly number[] = [0x6b4a2f, 0x2b3b5c, 0x4b5a3a, 0x7a2d24, 0x8c8c86, 0xb8964a, 0x1f1f24, 0x5c3b4e, 0xc9b79a, 0x36524f];
export const PED_TROUSERS: readonly number[] = [0x1d1d22, 0x34302a, 0x3d4757, 0x594a37, 0x24303a];
export const SKIN_TONES: readonly number[] = [0xe8c4a0, 0xd9a577, 0xb67b4f, 0x8a5a3b, 0x5e3d28, 0xeacbae];
export const HAIR_TONES: readonly number[] = [0x1a1410, 0x3a2a1c, 0x5a4028, 0x8a6a42, 0x9a9690, 0x2a2420];
export const SHIRTS: readonly number[] = [0xd8d2c4, 0xc8c0a8, 0xa9b4bc, 0xd9c48a, 0xb9a48c];
const SHOES: readonly number[] = [SWATCH.shoeBlack, SWATCH.shoeBrown, 0x241c18];
const HEADWEAR_TONES: readonly number[] = [SWATCH.capTweed, 0x1c1c20, 0x4a3a2a, 0x3d4757, 0x5a5048];
const SCARF_TONES: readonly number[] = [0x7a2d24, 0xb8964a, 0x36524f, 0x5c3b4e, 0x9a8a70];
/** Ties and knitwear accents: maroon, bottle, mustard, navy, brown, regimental stripes read as these. */
const TIES: readonly number[] = [0x6a2420, 0x2f4a3a, 0xa8873e, 0x26304a, 0x4a3426, 0x5c3b4e];
/** Young women's colours (still muted): teal, burnt orange, cream, mauve, olive, rust. */
const MOD_COLOURS: readonly number[] = [0x2f5d62, 0xa25a2a, 0xcfc3a5, 0x7d5a73, 0x5d6236, 0x8a3b26];
const GREYS: readonly number[] = [0x9a9690, 0xb8b4ac, 0x7d7a74];

/** The player: knee-length camel overcoat, dark trousers, tweed flat cap — a 1970 South London villain. */
export const PLAYER_VILLAIN: Readonly<CharacterStyle> = Object.freeze({
  sculpt: 'villain',
  build: 'stocky',
  height: 1.0,
  palette: Object.freeze({
    coat: SWATCH.camel,
    trousers: SWATCH.charcoal,
    shoes: SWATCH.shoeBlack,
    skin: 0xd2a587,
    hair: 0x2a1e16,
    shirt: SWATCH.shirtWhite,
    headwear: SWATCH.capTweed,
    accent: 0x2a2620,
  }),
  headwear: 'flatCap',
  outfit: 'longCoat',
  moustache: true,
  facial: 'mutton',
  hair: 'sidePart',
  age: 0.35,
  tie: true,
  swagger: 0.55,
});

/** Borough constable: navy tunic and trousers, custodian helmet with brass crest. */
export const POLICE_CONSTABLE: Readonly<CharacterStyle> = Object.freeze({
  build: 'average',
  height: 1.02,
  palette: Object.freeze({
    coat: SWATCH.policeNavy,
    trousers: SWATCH.policeNavyDark,
    shoes: SWATCH.shoeBlack,
    skin: 0xe0b48e,
    hair: 0x3a2a1c,
    shirt: 0xe8e6e0,
    headwear: SWATCH.policeNavyDark,
    accent: SWATCH.brass,
  }),
  headwear: 'policeHelmet',
  outfit: 'tunic',
  hair: 'short',
  age: 0.3,
  shoesKind: 'boots',
});

/**
 * Armed response officer: navy blouson over a white shirt and black tie, peaked cap with the
 * chequered band, a leather holster belt. Period issue, no modern tactical kit.
 */
export const POLICE_ARMED: Readonly<CharacterStyle> = Object.freeze({
  build: 'stocky',
  height: 1.03,
  palette: Object.freeze({
    coat: 0x1b2438,
    trousers: SWATCH.policeNavyDark,
    shoes: SWATCH.shoeBlack,
    skin: 0xd9a98a,
    hair: 0x2a2018,
    shirt: 0xe8e6e0,
    headwear: 0x141a2a,
    accent: 0x141414,
  }),
  headwear: 'peakedCap',
  outfit: 'armed',
  hair: 'short',
  facial: 'moustache',
  age: 0.4,
  tie: true,
  shoesKind: 'boots',
});

function preset(s: CharacterStyle): Readonly<CharacterStyle> {
  return Object.freeze({ ...s, palette: Object.freeze({ ...s.palette }) });
}

/** Authored pedestrian looks; randomPedStyle() draws the crowd from the archetypes below. */
export const PED_PRESETS = {
  clerk: preset({
    build: 'slim',
    height: 1.02,
    palette: { coat: SWATCH.navy, trousers: 0x24303a, shoes: SWATCH.shoeBlack, skin: 0xe8c4a0, hair: 0x3a2a1c, shirt: SWATCH.shirtWhite, headwear: 0x1c1c20, accent: 0x7a2d24 },
    headwear: 'trilby',
    outfit: 'suit',
    hair: 'sidePart',
    tie: true,
    glasses: true,
    prop: 'briefcase',
  }),
  docker: preset({
    build: 'heavy',
    height: 1.0,
    palette: { coat: SWATCH.donkey, trousers: 0x34302a, shoes: SWATCH.shoeBrown, skin: 0xd9a577, hair: 0x5a4028, shirt: 0xc8c0a8, headwear: SWATCH.capTweed, accent: 0x111214 },
    headwear: 'flatCap',
    outfit: 'donkeyJacket',
    facial: 'sideburns',
    shoesKind: 'boots',
    age: 0.45,
    smoker: true,
  }),
  housewife: preset({
    build: 'average',
    height: 0.94,
    palette: { coat: SWATCH.mustard, trousers: 0x594a37, shoes: SWATCH.shoeBrown, skin: 0xeacbae, hair: 0x8a6a42, shirt: 0xd9c48a, headwear: 0x7a2d24, accent: 0x5c3b4e },
    headwear: 'headscarf',
    outfit: 'dressSkirt',
    hair: 'bob',
    female: true,
    prop: 'shopping',
    age: 0.45,
  }),
  labourer: preset({
    build: 'stocky',
    height: 0.98,
    palette: { coat: SWATCH.olive, trousers: 0x3d4757, shoes: SWATCH.shoeBrown, skin: 0xb67b4f, hair: 0x1a1410, shirt: 0xa9b4bc, headwear: SWATCH.capTweed, accent: 0x2a2620 },
    headwear: 'none',
    outfit: 'shirtsleeves',
    hair: 'short',
    shoesKind: 'boots',
  }),
  gent: preset({
    build: 'average',
    height: 1.05,
    palette: { coat: SWATCH.grey, trousers: 0x1d1d22, shoes: SWATCH.shoeBlack, skin: 0x8a5a3b, hair: 0x9a9690, shirt: SWATCH.shirtWhite, headwear: 0x1c1c20, accent: 0x7a2d24 },
    headwear: 'bowler',
    outfit: 'longCoat',
    hair: 'balding',
    facial: 'moustache',
    tie: true,
    age: 0.65,
    prop: 'umbrella',
  }),
  milkman: preset({
    build: 'average',
    height: 0.99,
    palette: { coat: SWATCH.dairyWhite, trousers: 0x23262e, shoes: SWATCH.shoeBlack, skin: 0xe2b896, hair: 0x5a4028, shirt: 0xc8c0a8, headwear: 0x1f2a3e, accent: 0x1f2a3e },
    headwear: 'peakedCap',
    outfit: 'workCoat',
    hair: 'short',
    prop: 'crate',
    age: 0.4,
  }),
  vicar: preset({
    build: 'slim',
    height: 1.03,
    palette: { coat: SWATCH.clergy, trousers: 0x17171b, shoes: SWATCH.shoeBlack, skin: 0xe8c4a0, hair: 0x9a9690, shirt: 0x26262c, headwear: 0x17171b, accent: 0x17171b },
    headwear: 'none',
    outfit: 'clerical',
    hair: 'balding',
    glasses: true,
    age: 0.6,
    prop: 'newspaper',
  }),
} as const satisfies Record<string, Readonly<CharacterStyle>>;

const BUILDS: readonly CharacterBuild[] = ['slim', 'average', 'stocky', 'heavy'];
const BUILD_WEIGHTS: readonly number[] = [0.3, 0.4, 0.2, 0.1];

function pick<T>(rng: StyleRng, items: readonly T[]): T {
  return items[Math.min(items.length - 1, Math.floor(rng.next() * items.length))] as T;
}

function weighted<T>(rng: StyleRng, items: readonly T[], weights: readonly number[]): T {
  let total = 0;
  for (const w of weights) total += w;
  let r = rng.next() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i] ?? 0;
    if (r < 0) return items[i] as T;
  }
  return items[items.length - 1] as T;
}

/** Pedestrian archetypes. Each draws its own clothes, hair and props; colours come from the period lists. */
export type PedArchetype =
  | 'clerk'
  | 'gent'
  | 'docker'
  | 'labourer'
  | 'shopGirl'
  | 'housewife'
  | 'pensioner'
  | 'nan'
  | 'mod'
  | 'skinhead'
  | 'longhair'
  | 'milkman'
  | 'vicar'
  | 'local'
  | 'barmaid'
  | 'conductor'
  | 'trader'
  | 'rocker';
export const PED_ARCHETYPES: readonly PedArchetype[] = ['clerk', 'gent', 'docker', 'labourer', 'shopGirl', 'housewife', 'pensioner', 'nan', 'mod', 'skinhead', 'longhair', 'milkman', 'vicar', 'local', 'barmaid', 'conductor', 'trader', 'rocker'];

/** District character (src/world/lowBorough/data.ts ids) → archetype weights, same order as PED_ARCHETYPES. */
const DISTRICT_WEIGHTS: Record<string, readonly number[]> = {
  //                 clerk gent dock lab  shop wife pens nan  mod  skin long milk vic  local bar  cond trad rock
  default: /*     */ [10, 5, 6, 7, 9, 10, 7, 7, 6, 4, 4, 1.5, 1, 14, 3, 2, 3, 3],
  'the-parade': /**/ [14, 7, 3, 5, 14, 10, 6, 6, 9, 5, 5, 1.5, 1, 12, 3, 3, 6, 3],
  'mercy-square': [12, 9, 2, 4, 9, 9, 9, 9, 6, 3, 6, 1.5, 2, 12, 3, 4, 2, 2],
  'tanners-row': [6, 3, 6, 9, 6, 15, 10, 10, 6, 6, 3, 2, 1.5, 16, 4, 2, 3, 3],
  'cinder-yards': [3, 2, 14, 16, 3, 6, 5, 4, 4, 7, 3, 1, 0.5, 14, 2, 1, 2, 5],
  'gullet-basin': [3, 1, 22, 16, 2, 4, 4, 3, 3, 6, 2, 0.5, 0.5, 12, 2, 1, 2, 4],
  'kiln-common': [6, 3, 5, 12, 9, 13, 8, 8, 6, 5, 4, 2, 1, 15, 3, 2, 7, 3],
};

const male = (rng: StyleRng, o: { young?: boolean; old?: boolean } = {}): HairStyle => {
  if (o.old) return weighted(rng, ['balding', 'short', 'sidePart'] as const, [5, 3, 3]);
  if (o.young) return weighted(rng, ['mod', 'quiff', 'sidePart', 'long', 'short'] as const, [3, 2, 2, 2, 1]);
  return weighted(rng, ['short', 'sidePart', 'quiff', 'balding', 'mod'] as const, [4, 4, 2, 2, 1]);
};
const maleFace = (rng: StyleRng, old = false): FacialHair =>
  weighted(rng, ['none', 'moustache', 'sideburns', 'mutton', 'beard'] as const, old ? [4, 4, 1, 1, 1] : [5, 2, 3, 1.5, 0.7]);

/**
 * Seeded pedestrian. Same rng state in → same style out. `district` (a district id) weights the
 * archetypes: dockers and labourers round the basin and the yards, clerks and shop girls on The
 * Parade, pensioners and housewives in the terraces.
 */
export function randomPedStyle(rng: StyleRng, district?: string): CharacterStyle {
  const weights = (district && DISTRICT_WEIGHTS[district]) || DISTRICT_WEIGHTS.default!;
  const kind = weighted(rng, PED_ARCHETYPES, weights);
  return archetypeStyle(kind, rng);
}

/** One pedestrian of a given archetype (colours, hair, face and props drawn from `rng`). */
export function archetypeStyle(kind: PedArchetype, rng: StyleRng): CharacterStyle {
  let build = weighted(rng, BUILDS, BUILD_WEIGHTS);
  const skin = pick(rng, SKIN_TONES);
  const afroChance = skin === 0x5e3d28 || skin === 0x8a5a3b ? 0.45 : 0;
  let hair = pick(rng, HAIR_TONES);
  const base: CharacterPalette = {
    coat: pick(rng, PED_COATS),
    trousers: pick(rng, PED_TROUSERS),
    shoes: pick(rng, SHOES),
    skin,
    hair,
    shirt: pick(rng, SHIRTS),
    headwear: pick(rng, HEADWEAR_TONES),
    accent: pick(rng, TIES),
  };
  const r = rng.next();
  const s: CharacterStyle = { build, height: 0.97 + rng.next() * 0.1, palette: base, headwear: 'none', outfit: 'jacket', age: 0.3, facial: 'none', hair: 'short' };
  const afro = (): boolean => rng.next() < afroChance;
  switch (kind) {
    case 'clerk': {
      s.outfit = r < 0.6 ? 'suit' : r < 0.85 ? 'longCoat' : 'jacket';
      if (s.outfit === 'suit') base.trousers = base.coat === 0xc9b79a ? 0x594a37 : base.coat;
      s.headwear = rng.next() < 0.35 ? 'trilby' : 'none';
      s.hair = afro() ? 'afro' : male(rng);
      s.facial = rng.next() < 0.25 ? 'moustache' : 'none';
      s.tie = true;
      s.glasses = rng.next() < 0.35;
      s.prop = weighted(rng, ['briefcase', 'newspaper', 'umbrella', 'none'] as const, [4, 2, 2, 3]);
      s.age = 0.2 + rng.next() * 0.4;
      break;
    }
    case 'gent': {
      s.outfit = 'longCoat';
      base.coat = pick(rng, [SWATCH.grey, SWATCH.charcoal, SWATCH.camel, SWATCH.navy, 0x3a3a3c]);
      base.trousers = 0x1d1d22;
      s.headwear = weighted(rng, ['bowler', 'trilby', 'none'] as const, [3, 3, 1]);
      base.headwear = 0x1c1c20;
      base.hair = pick(rng, GREYS);
      s.hair = male(rng, { old: true });
      s.facial = weighted(rng, ['none', 'moustache'] as const, [1, 2]);
      s.tie = true;
      s.prop = weighted(rng, ['umbrella', 'newspaper', 'briefcase'] as const, [3, 2, 1]);
      s.age = 0.55 + rng.next() * 0.3;
      s.height = 1.0 + rng.next() * 0.08;
      break;
    }
    case 'docker': {
      s.swagger = 0.35;
      s.outfit = weighted(rng, ['donkeyJacket', 'jacket', 'shirtsleeves'] as const, [5, 2, 2]);
      if (s.outfit === 'donkeyJacket') {
        base.coat = pick(rng, [SWATCH.donkey, 0x2a2c2e, 0x1e2a36]);
        base.accent = 0x111214;
      }
      base.trousers = pick(rng, [0x34302a, 0x3d4757, 0x2a2622]);
      s.headwear = rng.next() < 0.7 ? 'flatCap' : 'none';
      s.hair = male(rng);
      s.facial = maleFace(rng);
      s.shoesKind = 'boots';
      s.smoker = rng.next() < 0.6;
      build = s.build = weighted(rng, BUILDS, [0.05, 0.3, 0.4, 0.25]);
      s.age = 0.25 + rng.next() * 0.45;
      break;
    }
    case 'labourer': {
      s.outfit = weighted(rng, ['shirtsleeves', 'knit', 'jacket'] as const, [3, 3, 2]);
      if (s.outfit === 'knit') base.coat = pick(rng, [0x5a4a36, 0x3d4757, 0x4b5a3a, 0x6b5a46, 0x7a2d24]);
      s.headwear = rng.next() < 0.45 ? 'flatCap' : 'none';
      s.hair = afro() ? 'afro' : male(rng);
      s.facial = maleFace(rng);
      s.shoesKind = 'boots';
      s.smoker = rng.next() < 0.4;
      s.age = 0.2 + rng.next() * 0.45;
      break;
    }
    case 'shopGirl': {
      s.outfit = weighted(rng, ['miniSkirt', 'dressSkirt'] as const, [3, 2]);
      s.female = true;
      base.coat = pick(rng, MOD_COLOURS);
      base.trousers = pick(rng, [0x2a2420, 0x3a2e28, 0x6a5040]); // tights
      base.shirt = pick(rng, [0xe8e2d4, 0xd9c48a, 0xcfc3a5]);
      base.accent = pick(rng, [0xe8e2d4, 0x1f1f24, 0x8a3b26]);
      s.hair = afro() ? 'afro' : weighted(rng, ['beehive', 'bob', 'long'] as const, [2, 3, 3]);
      s.shoesKind = 'heels';
      s.prop = rng.next() < 0.6 ? 'handbag' : 'none';
      s.smoker = rng.next() < 0.2;
      s.height = 0.93 + rng.next() * 0.08;
      s.build = rng.next() < 0.7 ? 'slim' : 'average';
      s.age = 0.1 + rng.next() * 0.2;
      break;
    }
    case 'housewife': {
      s.outfit = weighted(rng, ['dressSkirt', 'longCoat'] as const, [3, 2]);
      s.female = true;
      base.coat = pick(rng, [SWATCH.mustard, SWATCH.plum, SWATCH.bottleGreen, SWATCH.tobacco, SWATCH.stone, 0x6a6058, 0x7a2d24]);
      base.trousers = pick(rng, [0x594a37, 0x6a5848, 0x3a302a]);
      s.headwear = rng.next() < 0.55 ? 'headscarf' : 'none';
      base.headwear = pick(rng, SCARF_TONES);
      s.hair = weighted(rng, ['bob', 'beehive', 'short'] as const, [4, 2, 1]);
      s.prop = weighted(rng, ['shopping', 'handbag', 'none'] as const, [4, 3, 2]);
      s.height = 0.92 + rng.next() * 0.08;
      s.age = 0.3 + rng.next() * 0.3;
      break;
    }
    case 'pensioner': {
      s.outfit = weighted(rng, ['longCoat', 'jacket', 'knit'] as const, [3, 2, 1]);
      base.coat = pick(rng, [SWATCH.grey, SWATCH.tobacco, 0x5a5048, SWATCH.olive, 0x4a3a2a]);
      s.headwear = weighted(rng, ['flatCap', 'trilby', 'none'] as const, [4, 2, 1]);
      base.hair = pick(rng, GREYS);
      s.hair = male(rng, { old: true });
      s.facial = maleFace(rng, true);
      s.tie = rng.next() < 0.7;
      s.glasses = rng.next() < 0.5;
      s.prop = rng.next() < 0.5 ? 'stick' : 'newspaper';
      s.age = 0.8 + rng.next() * 0.2;
      s.height = 0.93 + rng.next() * 0.07;
      break;
    }
    case 'nan': {
      s.outfit = 'longCoat';
      s.female = true;
      base.coat = pick(rng, [SWATCH.plum, SWATCH.bottleGreen, 0x6a6058, SWATCH.oxblood, SWATCH.navy, 0x8a7a62]);
      base.trousers = pick(rng, [0x8a7a68, 0x6a5848]); // thick stockings
      s.headwear = rng.next() < 0.6 ? 'headscarf' : 'none';
      base.headwear = pick(rng, SCARF_TONES);
      base.hair = pick(rng, GREYS);
      s.hair = weighted(rng, ['bob', 'short'] as const, [3, 2]);
      s.glasses = rng.next() < 0.5;
      s.prop = weighted(rng, ['shopping', 'handbag', 'stick'] as const, [3, 3, 1]);
      s.age = 0.8 + rng.next() * 0.2;
      s.height = 0.9 + rng.next() * 0.06;
      s.build = rng.next() < 0.5 ? 'average' : 'stocky';
      break;
    }
    case 'mod': {
      s.swagger = 0.6;
      s.outfit = weighted(rng, ['parka', 'suit', 'harrington'] as const, [4, 3, 2]);
      if (s.outfit === 'parka') base.coat = pick(rng, [SWATCH.parkaOlive, 0x5d6236, 0x4b5a3a]);
      if (s.outfit === 'suit') base.trousers = base.coat = pick(rng, [0x3a3e44, 0x2b3b5c, 0x4a4238, 0x2a2c30]);
      s.hair = 'mod';
      s.tie = s.outfit !== 'harrington' || rng.next() < 0.3;
      base.accent = pick(rng, [0x1f1f24, 0x26304a, 0x6a2420]);
      s.build = rng.next() < 0.7 ? 'slim' : 'average';
      s.smoker = rng.next() < 0.4;
      s.age = 0.05 + rng.next() * 0.15;
      break;
    }
    case 'skinhead': {
      s.swagger = 0.9;
      s.outfit = weighted(rng, ['harrington', 'donkeyJacket', 'shirtsleeves'] as const, [4, 2, 2]);
      if (s.outfit === 'harrington') base.coat = pick(rng, [0x2a2c30, 0x5a5444, 0x2b3b5c, 0x7a2d24]);
      base.trousers = pick(rng, [0x3d4a5e, 0x2c3646]); // turned-up denim
      base.shirt = pick(rng, [0xa9b4bc, 0xc8c0a8, 0xb9a48c]);
      base.shoes = pick(rng, [0x3a1612, SWATCH.shoeBlack]); // ox-blood boots
      s.hair = 'crop';
      s.shoesKind = 'boots';
      s.smoker = rng.next() < 0.5;
      s.build = rng.next() < 0.6 ? 'average' : 'stocky';
      s.age = 0.05 + rng.next() * 0.15;
      break;
    }
    case 'longhair': {
      s.outfit = weighted(rng, ['parka', 'knit', 'jacket'] as const, [2, 2, 2]);
      if (s.outfit === 'knit') base.coat = pick(rng, MOD_COLOURS);
      base.trousers = pick(rng, [0x3d4a5e, 0x594a37, 0x4a3a2a]);
      s.hair = 'long';
      s.facial = weighted(rng, ['none', 'beard', 'moustache'] as const, [3, 2, 2]);
      s.smoker = rng.next() < 0.4;
      s.age = 0.1 + rng.next() * 0.15;
      break;
    }
    case 'milkman': {
      Object.assign(base, { coat: SWATCH.dairyWhite, headwear: 0x1f2a3e, accent: 0x1f2a3e, trousers: 0x23262e });
      s.outfit = 'workCoat';
      s.headwear = 'peakedCap';
      s.hair = male(rng);
      s.facial = maleFace(rng);
      s.prop = 'crate';
      s.age = 0.3 + rng.next() * 0.3;
      break;
    }
    case 'vicar': {
      Object.assign(base, { coat: SWATCH.clergy, trousers: 0x17171b, shirt: 0x26262c, accent: 0x17171b, shoes: SWATCH.shoeBlack });
      s.outfit = 'clerical';
      base.hair = rng.next() < 0.6 ? pick(rng, GREYS) : hair;
      s.hair = male(rng, { old: true });
      s.glasses = rng.next() < 0.6;
      s.prop = rng.next() < 0.5 ? 'newspaper' : 'none';
      s.age = 0.45 + rng.next() * 0.4;
      break;
    }
    case 'barmaid': {
      // Out on her break: a dark dress or a skirt suit, a white blouse collar, a cloth apron, beehive
      // or bob, heels, often a cigarette.
      s.outfit = rng.next() < 0.7 ? 'dressSkirt' : 'miniSkirt';
      s.female = true;
      base.coat = pick(rng, [0x1f1f24, SWATCH.plum, 0x2f5d62, 0x5c3b4e, 0x3a3a3c, SWATCH.oxblood]);
      base.trousers = pick(rng, [0x2a2420, 0x3a2e28, 0x6a5040]);
      base.shirt = pick(rng, [0xe8e2d4, 0xd9c48a]);
      base.accent = pick(rng, [0xe8e2d4, 0x1f1f24]);
      s.hair = afro() ? 'afro' : weighted(rng, ['beehive', 'bob', 'long'] as const, [4, 3, 1]);
      s.kit = 'apron';
      s.kitColour = pick(rng, [0xe6e2d8, 0xd9d5c8, 0x2a2a2e]);
      s.shoesKind = 'heels';
      s.smoker = rng.next() < 0.5;
      s.height = 0.93 + rng.next() * 0.08;
      s.build = weighted(rng, BUILDS, [0.45, 0.4, 0.15, 0]);
      s.age = 0.15 + rng.next() * 0.35;
      break;
    }
    case 'conductor': {
      // Bus conductor: grey-blue jacket and trousers, peaked cap, the ticket machine on his chest and
      // the cash bag on his hip.
      const cloth = pick(rng, [0x3d4757, 0x2b3b5c, 0x34404a]);
      Object.assign(base, { coat: cloth, trousers: cloth, headwear: cloth, accent: 0x1c1c20, shirt: pick(rng, [SWATCH.shirtWhite, 0xa9b4bc]) });
      s.outfit = 'jacket';
      s.headwear = 'peakedCap';
      s.hair = male(rng);
      s.facial = weighted(rng, ['none', 'moustache', 'sideburns'] as const, [3, 2, 1]);
      s.tie = true;
      s.kit = 'conductor';
      s.build = weighted(rng, BUILDS, [0.2, 0.45, 0.25, 0.1]);
      s.age = 0.3 + rng.next() * 0.35;
      break;
    }
    case 'trader': {
      // Market trader: shirtsleeves or a jumper under a brown apron, a flat cap, a fag on; built like
      // a man who lifts crates.
      s.outfit = weighted(rng, ['shirtsleeves', 'knit'] as const, [3, 2]);
      if (s.outfit === 'knit') base.coat = pick(rng, [0x5a4a36, 0x3d4757, 0x6b5a46]);
      base.trousers = pick(rng, [0x34302a, 0x3d4757, 0x594a37]);
      s.headwear = rng.next() < 0.7 ? 'flatCap' : 'none';
      s.hair = afro() ? 'afro' : male(rng);
      s.facial = maleFace(rng);
      s.kit = 'apron';
      s.kitColour = pick(rng, [0x5a5040, 0x4a4538, 0x6a5a46, 0x3a3e44]);
      s.smoker = rng.next() < 0.5;
      s.prop = rng.next() < 0.3 ? 'crate' : 'none';
      s.build = weighted(rng, BUILDS, [0.05, 0.3, 0.4, 0.25]);
      s.swagger = 0.3;
      s.age = 0.3 + rng.next() * 0.35;
      break;
    }
    case 'rocker': {
      // Rocker: black leather jacket, quiff and sideburns, drainpipe jeans and boots.
      s.outfit = 'harrington';
      base.coat = pick(rng, [0x1a1a1e, 0x1f1b1a, 0x24201e]);
      base.accent = base.coat;
      base.trousers = pick(rng, [0x2c3646, 0x1d1d22, 0x3d4a5e]);
      base.shirt = pick(rng, [0xd8d2c4, 0x1f1f24, 0x7a2d24]);
      base.shoes = SWATCH.shoeBlack;
      base.hair = pick(rng, [0x1a1410, 0x2a2420, 0x3a2a1c]);
      s.hair = 'quiff';
      s.facial = rng.next() < 0.6 ? 'sideburns' : 'none';
      s.shoesKind = 'boots';
      s.smoker = rng.next() < 0.6;
      s.build = rng.next() < 0.6 ? 'slim' : 'average';
      s.swagger = 0.8;
      s.age = 0.05 + rng.next() * 0.2;
      break;
    }
    default: {
      // The long tail: anybody about the borough.
      const skirt = rng.next() < 0.35;
      if (skirt) {
        s.female = true;
        s.outfit = weighted(rng, ['dressSkirt', 'longCoat', 'miniSkirt'] as const, [3, 3, 1]);
        s.headwear = rng.next() < 0.3 ? 'headscarf' : 'none';
        base.headwear = pick(rng, SCARF_TONES);
        s.hair = afro() ? 'afro' : weighted(rng, ['bob', 'long', 'beehive', 'short'] as const, [4, 2, 1, 1]);
        s.prop = rng.next() < 0.5 ? 'handbag' : 'none';
        s.height = 0.92 + rng.next() * 0.08;
        s.shoesKind = s.outfit === 'miniSkirt' ? 'heels' : 'shoes';
      } else {
        s.outfit = weighted(rng, ['longCoat', 'jacket', 'shirtsleeves', 'knit', 'harrington'] as const, [3, 3.5, 1.5, 1.5, 1]);
        s.headwear = weighted(rng, ['flatCap', 'trilby', 'none'] as const, [3, 2, 5]);
        s.hair = afro() ? 'afro' : male(rng);
        s.facial = maleFace(rng);
        s.tie = s.outfit === 'longCoat' || (s.outfit === 'jacket' && rng.next() < 0.6);
        s.smoker = rng.next() < 0.25;
      }
      s.age = 0.15 + rng.next() * 0.6;
      break;
    }
  }
  if (s.age! > 0.75 && !s.female && s.hair !== 'afro') base.hair = pick(rng, GREYS);
  // Wear and dirt: working clothes are grubbier and faded, boots scuffed.
  const grime = kind === 'docker' || kind === 'labourer' ? 0.1 + rng.next() * 0.15 : kind === 'skinhead' || kind === 'longhair' || kind === 'local' ? rng.next() * 0.08 : 0;
  if (grime > 0) {
    base.coat = worn(base.coat, grime);
    base.trousers = worn(base.trousers, grime * 1.4);
    base.shoes = worn(base.shoes, grime * 0.5);
  }
  s.moustache = s.facial === 'moustache' || s.facial === 'mutton' || s.facial === 'beard';
  return s;
}

/** Darken and grey a colour toward a dusty brown by `k` (0..1): wear, soot and dirt. */
export function worn(hex: number, k: number): number {
  const dust = [0x4a, 0x42, 0x36];
  const ch = (shift: number, i: number): number => {
    const v = (hex >> shift) & 255;
    return Math.max(0, Math.min(255, Math.round((v + (dust[i]! - v) * k * 0.6) * (1 - k * 0.35))));
  };
  return (ch(16, 0) << 16) | (ch(8, 1) << 8) | ch(0, 2);
}

/** Stable string key for caching geometry/material sets per style. */
export function styleKey(s: CharacterStyle): string {
  const p = s.palette;
  return [
    s.build,
    s.height.toFixed(3),
    s.headwear,
    s.outfit,
    p.coat,
    p.trousers,
    p.shoes,
    p.skin,
    p.hair,
    p.shirt,
    p.headwear,
    p.accent,
    s.moustache ? 1 : 0,
    s.facial ?? '',
    s.hair ?? '',
    (s.age ?? 0.3).toFixed(2),
    s.female ? 1 : 0,
    s.tie ? 1 : 0,
    s.glasses ? 1 : 0,
    s.smoker ? 1 : 0,
    s.shoesKind ?? '',
    s.prop ?? '',
    s.kit ?? '',
    s.kitColour ?? '',
    s.sculpt ?? '',
  ].join('|');
}

/** Tiny seeded RNG (mulberry32) so per-id styles need no game RNG stream. */
function idRng(id: number): StyleRng {
  let s = (Math.imul((id | 0) ^ 0x5bd1e995, 0x9e3779b1) ^ 0x2545f491) | 0;
  return {
    next(): number {
      s = (s + 0x6d2b79f5) | 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    },
  };
}

/**
 * The look of pedestrian `id`: deterministic, so a pedestrian keeps its clothes for its whole life
 * (and a remote client rendering the same id in the same district sees the same person). `district`
 * (where they first appear) weights the archetypes. Allocates — call on spawn only.
 */
export function pedStyleForId(id: number, district?: string): CharacterStyle {
  return randomPedStyle(idRng(id), district);
}
